import { useId, useMemo } from "react"

type AngledSolarSystemProps = {
  /** Radius of the center dot */
  centerDotRadius?: number
  /** Spacing between rings */
  ringSpacing?: number
  /** Number of orbital rings */
  numberOfRings?: number
  /** Number of dots per ring */
  dotsPerRing?: number
  /** Whether to render dots as outlines instead of fills */
  useOutlines?: boolean
  /** Size (radius) of dots on rings */
  dotSize?: number
  /** Initial radius of the first ring */
  initialRingRadius?: number
  /** Vertical offset added to each subsequent ring (creates stacked perspective) */
  verticalOffsetPerRing?: number
}

export const AngledSolarSystem = ({
  centerDotRadius = 40,
  ringSpacing = 35,
  numberOfRings = 4,
  dotsPerRing = 13,
  useOutlines = false,
  dotSize = 8,
  initialRingRadius = 90,
  verticalOffsetPerRing = 0,
}: AngledSolarSystemProps = {}) => {
  // Generate unique ID for this component instance to avoid mask ID collisions
  const componentId = useId()
  // All dots have the same radius
  const dotRadius = dotSize
  // Mask radius is slightly larger than dot radius to create border/shadow effect
  const maskRadius = dotRadius + 3

  // Generate planets and orbits
  const { orbits, planets, svgSize, centerX, centerY } = useMemo(() => {
    // Seeded random function for consistent rendering
    const seed = 42
    const random = (offset: number) => {
      const x = Math.sin(seed + offset) * 10000
      return x - Math.floor(x)
    }

    // Calculate SVG size based on parameters
    // Start radius is the initial ring radius
    const startRadius = initialRingRadius

    // Outermost ring radius
    const outermostRadius = startRadius + (numberOfRings - 1) * ringSpacing

    // SVG size is diameter of outermost ring + padding
    const padding = 40
    const svgSize = (outermostRadius + padding) * 2

    const centerX = svgSize / 2
    const centerY = svgSize / 2

    const orbitsData: Array<{ radius: number }> = []
    const planetsData: Array<{
      orbitIndex: number
      angle: number
    }> = []

    // Generate orbits and dots
    for (let i = 0; i < numberOfRings; i++) {
      const radius = startRadius + i * ringSpacing
      orbitsData.push({ radius })

      // Calculate minimum angular spacing to prevent dots from overlapping
      // For two dots with radius dotRadius on a circle of radius r,
      // minimum arc distance between centers = 2 * dotRadius (to prevent overlap)
      // Minimum angular separation = 2 * dotRadius / r
      // Add a small safety margin (1.2x) to ensure no overlap
      const minArcDistance = 2 * dotRadius
      const minAngularSpacing = (minArcDistance / radius) * 1.2

      // Generate dots with random spacing, ensuring minimum separation
      const angles: number[] = []
      const maxAttempts = dotsPerRing * 200 // Prevent infinite loops
      let attempts = 0

      while (angles.length < dotsPerRing && attempts < maxAttempts) {
        attempts++
        const candidateAngle = random(i * 10000 + angles.length * 100 + attempts) * 2 * Math.PI

        // Check if this angle is far enough from all existing angles
        const isValid = angles.every((existingAngle) => {
          // Calculate angular distance (handling wrap-around)
          let angularDist = Math.abs(candidateAngle - existingAngle)
          // Handle wrap-around (e.g., angle 0.1 and 6.1 are close)
          angularDist = Math.min(angularDist, 2 * Math.PI - angularDist)
          // Ensure the arc distance is at least the minimum required
          const arcDistance = radius * angularDist
          return arcDistance >= minArcDistance * 1.2
        })

        if (isValid) {
          angles.push(candidateAngle)
        }
      }

      // If we couldn't place all dots with random spacing, fall back to evenly spaced
      if (angles.length < dotsPerRing) {
        const angleStep = (2 * Math.PI) / dotsPerRing
        // Verify that evenly spaced dots won't overlap
        const evenlySpacedArcDistance = radius * angleStep
        if (evenlySpacedArcDistance >= minArcDistance * 1.2) {
          // Safe to use evenly spaced with small random offsets
          for (let j = 0; j < dotsPerRing; j++) {
            const baseAngle = j * angleStep
            // Add small random offset that respects minimum spacing
            const maxOffset = (angleStep - minAngularSpacing) * 0.5
            const offset = (random(i * 1000 + j * 100) - 0.5) * maxOffset
            angles.push(baseAngle + offset)
          }
        } else {
          // Not enough space for all dots - reduce count or increase spacing
          // Use minimum spacing between dots
          for (let j = 0; j < dotsPerRing; j++) {
            const angle = j * minAngularSpacing
            if (angle < 2 * Math.PI) {
              angles.push(angle)
            }
          }
        }
      }

      // Add all valid angles
      angles.forEach((angle) => {
        planetsData.push({
          orbitIndex: i,
          angle: angle,
        })
      })
    }

    return {
      orbits: orbitsData,
      planets: planetsData,
      svgSize,
      centerX,
      centerY,
    }
  }, [numberOfRings, ringSpacing, dotsPerRing, initialRingRadius, dotRadius])

  // Calculate planet positions with manual 2D projection
  // instead of using CSS 3D transforms
  const rotationAngle = 60
  const rotationRad = (rotationAngle * Math.PI) / 180
  const baseScaleY = Math.cos(rotationRad)

  // Controls how much the vertical offset affects the ring's "tilt" (perspective)
  const PERSPECTIVE_WARP_FACTOR = 0.002

  const planetPositions = planets.map((planet) => {
    const orbit = orbits[planet.orbitIndex]
    const ringScaleY = Math.max(
      0.05,
      baseScaleY + planet.orbitIndex * verticalOffsetPerRing * PERSPECTIVE_WARP_FACTOR,
    )

    const x = centerX + orbit.radius * Math.cos(planet.angle)
    const y =
      centerY +
      orbit.radius * Math.sin(planet.angle) * ringScaleY +
      planet.orbitIndex * verticalOffsetPerRing
    return { ...planet, x, y }
  })

  return (
    <div className="flex items-center justify-center bg-bg-default p-8">
      <div className="relative">
        <svg
          width={svgSize}
          height={svgSize}
          viewBox={`0 0 ${svgSize} ${svgSize}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-full"
          style={{
            width: svgSize,
            height: svgSize,
          }}
        >
          {/* Background */}
          <rect width={svgSize} height={svgSize} className="fill-bg-default" />

          {/* Define masks for clipping rings at dot positions */}
          <defs>
            {orbits.map((_orbit, orbitIndex) => {
              // Find all dots on this ring
              const dotsOnRing = planetPositions.filter(
                (planet) => planet.orbitIndex === orbitIndex,
              )

              return (
                <mask
                  key={`ring-mask-${orbitIndex}`}
                  id={`angled-ring-mask-${componentId}-${orbitIndex}`}
                >
                  {/* White background (visible) */}
                  <rect width={svgSize} height={svgSize} fill="white" />
                  {/* Black circles (create holes/masks) - slightly larger than dots */}
                  {dotsOnRing.map((planet, dotIndex) => (
                    <circle
                      key={`mask-hole-${orbitIndex}-${dotIndex}`}
                      cx={planet.x}
                      cy={planet.y}
                      r={maskRadius}
                      fill="black"
                    />
                  ))}
                </mask>
              )
            })}
          </defs>

          {/* Orbital paths - ellipses to simulate 3D perspective */}
          {orbits.map((orbit, index) => {
            const ringScaleY = Math.max(
              0.05,
              baseScaleY + index * verticalOffsetPerRing * PERSPECTIVE_WARP_FACTOR,
            )

            return (
              <ellipse
                key={`orbit-${index}`}
                cx={centerX}
                cy={centerY + index * verticalOffsetPerRing}
                rx={orbit.radius}
                ry={orbit.radius * ringScaleY}
                className="stroke-fg-secondary/40"
                strokeWidth="1"
                strokeDasharray="2,2"
                fill="none"
                mask={`url(#angled-ring-mask-${componentId}-${index})`}
              />
            )
          })}

          {/* Dots - circles at projected positions */}
          {planetPositions.map((planet, index) => (
            <circle
              key={`dot-${index}`}
              cx={planet.x}
              cy={planet.y}
              r={dotRadius}
              className={useOutlines ? "stroke-fg-secondary/40 fill-none" : "fill-fg-secondary/40"}
              strokeWidth={useOutlines ? "1" : undefined}
            />
          ))}

          {/* Center dot */}
          <circle
            cx={centerX}
            cy={centerY}
            r={centerDotRadius}
            className={useOutlines ? "stroke-fg-secondary/40 fill-none" : "fill-fg-secondary/40"}
            strokeWidth={useOutlines ? "1" : undefined}
          />
        </svg>
      </div>
    </div>
  )
}

export default AngledSolarSystem
